/**
 * 审批流程统计导出 —— 统计配置（stats_config）共享工具
 * 配置模型（spec §4）：顶层方案数组 stats（本期 UI 限 1 个方案，模型预留多方案），
 * 清单 listSheet.columns + 汇总 summarySheet.dimensions/metrics
 * + 可选 detailSheet（明细行统计，2026-09-21 spec：detailKey + columns/dimensions/metrics，
 * detailSheet 缺省 = 不启用行级统计，存量配置零迁移兼容）
 * 校验规则与后端 StatsConfigValidator 同规则同文案，保存时前端拦截并定位首个错误
 */

// 配置版本号
export const STATS_CONFIG_VERSION = 1

// 清单系统列白名单（spec §4：含 status，与单据导出 SYSTEM_FIELDS 不同源，独立定义）
export const STATS_LIST_SYSTEM_COLUMNS = [
  { key: 'title', label: '审批标题' },
  { key: 'applicantName', label: '申请人' },
  { key: 'deptName', label: '部门' },
  { key: 'createTime', label: '提交时间' },
  { key: 'status', label: '状态' }
]

export const STATS_LIST_SYSTEM_COLUMN_KEYS = STATS_LIST_SYSTEM_COLUMNS.map(c => c.key)

// 汇总系统维度白名单（spec §4：month 按 createTime 截 yyyy-MM，后端口径）
export const STATS_SUMMARY_SYSTEM_DIMENSIONS = [
  { key: 'deptName', label: '部门' },
  { key: 'applicantName', label: '申请人' },
  { key: 'month', label: '月份' }
]

export const STATS_SUMMARY_SYSTEM_DIMENSION_KEYS = STATS_SUMMARY_SYSTEM_DIMENSIONS.map(d => d.key)

// 汇总字段维度类型白名单（spec §4：CHECKBOX 多值维度本期不支持，登记演进项）
export const STATS_DIMENSION_FIELD_TYPES = ['SELECT', 'RADIO', 'SWITCH', 'DATE', 'DATETIME']

// 明细汇总行内子字段维度类型白名单（2026-09-21 spec §4.2：子字段类型域无 SWITCH，
// 事实源 workflowDetail.js DETAIL_CHILD_TYPES / 后端 TemplateFormSchemaValidator.CHILD_ALLOWED_TYPES）
export const STATS_DETAIL_DIMENSION_FIELD_TYPES = ['SELECT', 'RADIO', 'DATE', 'DATETIME']

// 数值字段类型（SUM/AVG/MAX/MIN 限数值列：NUMBER/MONEY 顶层字段或 summary.targetKey）
export const STATS_NUMERIC_FIELD_TYPES = ['NUMBER', 'MONEY']

// 聚合方式（COUNT 的 key 必须为 *）
export const STATS_AGGREGATES = ['SUM', 'AVG', 'MAX', 'MIN', 'COUNT']

// COUNT 指标固定 key
export const STATS_COUNT_KEY = '*'

// 汇总维度数量范围（顺序即分组层级）
export const STATS_DIMENSION_MIN = 1
export const STATS_DIMENSION_MAX = 3

// 本期 UI 只允许 1 个统计方案（模型为数组预留多方案）
export const STATS_PLAN_MAX = 1

// 导出区间最大跨度（月，spec §5.3）
export const STATS_EXPORT_RANGE_MAX_MONTHS = 12

// 表单字段是否为 DETAIL 类型（对齐后端 equalsIgnoreCase 语义）
function isDetailField(field) {
  return String(field?.type ?? '').toUpperCase() === 'DETAIL'
}

/**
 * 清单 FIELD 列可用 key 全集：顶层非 DETAIL 字段 key ∪ summary.targetKey（spec §4，排除 DETAIL 容器本身）
 * @param {Array} fields 表单字段（getFieldList 结构：key/label/type/summary）
 * @returns {Set<string>}
 */
export function collectStatsListFieldKeys(fields) {
  const keys = new Set()
  ;(fields || []).forEach(field => {
    if (!field) return
    if (isDetailField(field)) {
      (field.summary || []).forEach(sum => {
        if (sum?.targetKey) keys.add(sum.targetKey)
      })
      return
    }
    if (field.key) keys.add(field.key)
  })
  return keys
}

/**
 * 数值列 key 全集：顶层 NUMBER/MONEY 字段 key ∪ summary.targetKey（SUM/AVG/MAX/MIN 限这些列）
 * @param {Array} fields 表单字段
 * @returns {Set<string>}
 */
export function collectStatsNumericKeys(fields) {
  const keys = new Set()
  ;(fields || []).forEach(field => {
    if (!field) return
    if (isDetailField(field)) {
      (field.summary || []).forEach(sum => {
        if (sum?.targetKey) keys.add(sum.targetKey)
      })
      return
    }
    if (field.key && STATS_NUMERIC_FIELD_TYPES.includes(String(field.type ?? '').toUpperCase())) {
      keys.add(field.key)
    }
  })
  return keys
}

/**
 * 字段类型查找（顶层 key → 大写 type；含 DETAIL 容器本身，供维度白名单与 DETAIL 排除判断）
 * @param {Array} fields 表单字段
 * @returns {Object}
 */
export function collectStatsFieldTypeMap(fields) {
  const map = {}
  ;(fields || []).forEach(field => {
    if (field?.key) map[field.key] = String(field.type ?? '').toUpperCase()
  })
  return map
}

/**
 * detailSheet.detailKey 候选全集：顶层 DETAIL 字段 key（2026-09-21 spec §4.2，一个方案只选一个 DETAIL）
 * @param {Array} fields 表单字段
 * @returns {Set<string>}
 */
export function collectStatsDetailKeys(fields) {
  const keys = new Set()
  ;(fields || []).forEach(field => {
    if (field?.key && isDetailField(field)) keys.add(field.key)
  })
  return keys
}

/**
 * 所选 DETAIL 的子字段数组（detailKey 不存在或非 DETAIL 时为空数组）
 * @param {Array} fields 表单字段
 * @param {string} detailKey 顶层 DETAIL 字段 key
 * @returns {Array}
 */
export function collectDetailChildren(fields, detailKey) {
  const detail = (fields || []).find(field => field?.key === detailKey && isDetailField(field))
  return Array.isArray(detail?.children) ? detail.children : []
}

/**
 * 子字段 key 全集（行级作用域：仅在所属 DETAIL 的 children 内解析，与顶层同名 key 不互相影响）
 * @param {Array} fields 表单字段
 * @param {string} detailKey 顶层 DETAIL 字段 key
 * @returns {Set<string>}
 */
export function collectDetailChildKeys(fields, detailKey) {
  const keys = new Set()
  collectDetailChildren(fields, detailKey).forEach(child => {
    if (child?.key) keys.add(child.key)
  })
  return keys
}

/**
 * 子字段类型查找（key → 大写 type）
 * @param {Array} fields 表单字段
 * @param {string} detailKey 顶层 DETAIL 字段 key
 * @returns {Object}
 */
export function collectDetailChildTypeMap(fields, detailKey) {
  const map = {}
  collectDetailChildren(fields, detailKey).forEach(child => {
    if (child?.key) map[child.key] = String(child.type ?? '').toUpperCase()
  })
  return map
}

/**
 * 子字段数值列 key 全集：子类型 NUMBER/MONEY（computed 仅允许挂在 NUMBER/MONEY 子字段上，本就被该白名单覆盖）
 * @param {Array} fields 表单字段
 * @param {string} detailKey 顶层 DETAIL 字段 key
 * @returns {Set<string>}
 */
export function collectDetailNumericChildKeys(fields, detailKey) {
  const keys = new Set()
  collectDetailChildren(fields, detailKey).forEach(child => {
    if (child?.key && STATS_NUMERIC_FIELD_TYPES.includes(String(child.type ?? '').toUpperCase())) {
      keys.add(child.key)
    }
  })
  return keys
}

/**
 * 收集统计配置中失效引用（字段已删除 / 类型不再满足白名单）的 key，用于设计器标黄警示（spec §4 联动标黄）
 * 行级作用域独立分组（2026-09-21 spec §4.2 前端契约）：子字段 key 只要求在所属 children 内唯一，
 * 可与顶层 key 重名，故 detailSheet 内引用查 detail.* 三个 Set，与顶层同名 key 不互标黄
 * @param {Object|null} config 统计配置 { version, stats }
 * @param {Array} fields 表单字段
 * @returns {{ columnKeys: Set<string>, dimensionKeys: Set<string>, metricKeys: Set<string>,
 *   detail: { columnKeys: Set<string>, dimensionKeys: Set<string>, metricKeys: Set<string> } }}
 */
export function collectInvalidStatsKeys(config, fields) {
  const invalid = {
    columnKeys: new Set(),
    dimensionKeys: new Set(),
    metricKeys: new Set(),
    detail: { columnKeys: new Set(), dimensionKeys: new Set(), metricKeys: new Set() }
  }
  if (!config || !Array.isArray(config.stats)) return invalid
  const listFieldKeys = collectStatsListFieldKeys(fields)
  const numericKeys = collectStatsNumericKeys(fields)
  const typeMap = collectStatsFieldTypeMap(fields)
  const detailKeys = collectStatsDetailKeys(fields)
  config.stats.forEach(plan => {
    (plan?.listSheet?.columns || []).forEach(col => {
      if (String(col?.type ?? '').toUpperCase() !== 'FIELD') return
      if (!listFieldKeys.has(col.key)) invalid.columnKeys.add(col.key)
    })
    ;(plan?.summarySheet?.dimensions || []).forEach(dim => {
      if (String(dim?.type ?? '').toUpperCase() !== 'FIELD') return
      // 字段被删除（typeMap 查不到）或类型不再是维度白名单，均算失效
      if (!STATS_DIMENSION_FIELD_TYPES.includes(typeMap[dim.key])) invalid.dimensionKeys.add(dim.key)
    })
    ;(plan?.summarySheet?.metrics || []).forEach(metric => {
      if (!metric || metric.key === STATS_COUNT_KEY) return
      // 字段被删除或不再是数值列，均算失效
      if (!numericKeys.has(metric.key)) invalid.metricKeys.add(metric.key)
    })
    // 明细行统计（detailSheet）：key 仅在所选 detailKey 的 children 内解析
    const detail = plan?.detailSheet
    if (!detail) return
    // detailKey 本身失效（被删除或不再是 DETAIL）时 children 无法解析，其内 FIELD 引用全部标黄
    const detailValid = detailKeys.has(detail.detailKey)
    const childKeys = collectDetailChildKeys(fields, detail.detailKey)
    const childTypeMap = collectDetailChildTypeMap(fields, detail.detailKey)
    const numericChildKeys = collectDetailNumericChildKeys(fields, detail.detailKey)
    ;(detail.columns || []).forEach(col => {
      if (String(col?.type ?? '').toUpperCase() !== 'FIELD') return
      if (!detailValid || !childKeys.has(col.key)) invalid.detail.columnKeys.add(col.key)
    })
    ;(detail.dimensions || []).forEach(dim => {
      if (String(dim?.type ?? '').toUpperCase() !== 'FIELD') return
      if (!detailValid || !STATS_DETAIL_DIMENSION_FIELD_TYPES.includes(childTypeMap[dim.key])) {
        invalid.detail.dimensionKeys.add(dim.key)
      }
    })
    ;(detail.metrics || []).forEach(metric => {
      if (!metric || metric.key === STATS_COUNT_KEY) return
      if (!detailValid || !numericChildKeys.has(metric.key)) invalid.detail.metricKeys.add(metric.key)
    })
  })
  return invalid
}

/**
 * 校验统计配置（与后端 StatsConfigValidator 同规则同文案同抛错顺序，spec §4）：
 * 方案数组长度 ≤1 → 逐方案（清单列逐列 → 维度数量 1-3 → 逐维度 → 指标 ≥1 → 逐指标）；
 * 指标内顺序：聚合方式合法性 → COUNT key 约束 → 字段存在性 → 数值类型约束。
 * 空配置（null/无 stats/空数组）直接放行（=不启用统计导出）。
 * @param {Object|null} config 统计配置 { version, stats }
 * @param {Array} fields 模板当前表单字段（getFieldList 结构）
 * @returns {string} 空串表示通过；否则为首个错误提示
 */
export function validateStatsConfig(config, fields) {
  if (!config || !Array.isArray(config.stats) || config.stats.length === 0) return ''
  if (config.stats.length > STATS_PLAN_MAX) {
    return `统计配置本期仅支持 ${STATS_PLAN_MAX} 个统计方案`
  }
  const listFieldKeys = collectStatsListFieldKeys(fields)
  const numericKeys = collectStatsNumericKeys(fields)
  const typeMap = collectStatsFieldTypeMap(fields)
  for (let i = 0; i < config.stats.length; i++) {
    const plan = config.stats[i]
    // 清单列：≥1 列；SYSTEM key 白名单；FIELD key ∈ 顶层非 DETAIL key ∪ targetKey（排除 DETAIL 容器）
    const columns = plan.listSheet?.columns || []
    if (columns.length === 0) {
      return `清单列至少配置 1 列`
    }
    for (const col of columns) {
      const type = String(col?.type ?? '').toUpperCase()
      if (type === 'SYSTEM') {
        if (!STATS_LIST_SYSTEM_COLUMN_KEYS.includes(col.key)) {
          return `清单列不支持的系统字段：${col.key}`
        }
      } else if (type === 'FIELD') {
        if (typeMap[col.key] === 'DETAIL') {
          return `清单列不支持明细表字段：${col.key}`
        }
        if (!listFieldKeys.has(col.key)) {
          return `清单列引用了已删除的字段：${col.key}`
        }
      } else {
        return `清单列存在不支持的列类型：${col?.type}`
      }
    }
    // 汇总维度：1-3 个；SYSTEM key 白名单；FIELD 限 SELECT/RADIO/SWITCH/DATE/DATETIME
    const dimensions = plan.summarySheet?.dimensions || []
    if (dimensions.length < STATS_DIMENSION_MIN || dimensions.length > STATS_DIMENSION_MAX) {
      return `汇总维度需 ${STATS_DIMENSION_MIN}-${STATS_DIMENSION_MAX} 个`
    }
    for (const dim of dimensions) {
      const type = String(dim?.type ?? '').toUpperCase()
      if (type === 'SYSTEM') {
        if (!STATS_SUMMARY_SYSTEM_DIMENSION_KEYS.includes(dim.key)) {
          return `汇总维度不支持的系统字段：${dim.key}`
        }
      } else if (type === 'FIELD') {
        if (!(dim.key in typeMap)) {
          return `汇总维度引用了已删除的字段：${dim.key}`
        }
        if (!STATS_DIMENSION_FIELD_TYPES.includes(typeMap[dim.key])) {
          return `汇总维度字段类型不支持（仅 SELECT/RADIO/SWITCH/DATE/DATETIME）：${dim.key}`
        }
      } else {
        return `汇总维度存在不支持的维度类型：${dim?.type}`
      }
    }
    // 汇总指标：≥1 个；SUM/AVG/MAX/MIN 限数值列；COUNT 的 key 必须为 *
    const metrics = plan.summarySheet?.metrics || []
    if (metrics.length === 0) {
      return `汇总指标至少配置 1 个`
    }
    for (const metric of metrics) {
      const aggregate = String(metric?.aggregate ?? '').toUpperCase()
      if (!STATS_AGGREGATES.includes(aggregate)) {
        return `汇总指标不支持的聚合方式：${metric?.aggregate}`
      }
      if (aggregate === 'COUNT') {
        if (metric.key !== STATS_COUNT_KEY) {
          return `COUNT 指标的 key 必须为 *`
        }
        continue
      }
      // targetKey 不在 typeMap 但在 numericKeys，属合法数值列；两者都查不到才算已删除
      if (!(metric.key in typeMap) && !numericKeys.has(metric.key)) {
        return `汇总指标引用了已删除的字段：${metric.key}`
      }
      if (!numericKeys.has(metric.key)) {
        return `${aggregate} 指标仅支持数值字段：${metric.key}`
      }
    }
    // 明细行统计（2026-09-21 spec §4.2）：detailSheet 为 null 时跳过（向后兼容）
    const detailError = validateDetailSheet(plan.detailSheet, fields)
    if (detailError) return detailError
  }
  return ''
}

/**
 * 校验 detailSheet（2026-09-21 spec §4.2，与后端 StatsConfigValidator.validateDetailSheet 同规则同文案同顺序）：
 * detailKey（空白/已删除/非 DETAIL 三种语义分别报错）→ 明细清单列 → 明细汇总维度（1-3，类型白名单无 SWITCH）→ 明细汇总指标（限子字段数值列）。
 * @param {Object|null} detailSheet 明细行统计配置 { detailKey, columns, dimensions, metrics }
 * @param {Array} fields 模板当前表单字段
 * @returns {string} 空串表示通过；否则为首个错误提示
 */
function validateDetailSheet(detailSheet, fields) {
  if (!detailSheet) return ''
  const typeMap = collectStatsFieldTypeMap(fields)
  // 规则 1：detailKey 必填；不存在与非 DETAIL 两种语义分别报错
  if (!detailSheet.detailKey || String(detailSheet.detailKey).trim() === '') {
    return `明细行统计缺少明细表字段`
  }
  if (!(detailSheet.detailKey in typeMap)) {
    return `明细行统计引用了已删除的字段：${detailSheet.detailKey}`
  }
  if (typeMap[detailSheet.detailKey] !== 'DETAIL') {
    return `明细行统计不支持非明细表字段：${detailSheet.detailKey}`
  }
  const childKeys = collectDetailChildKeys(fields, detailSheet.detailKey)
  const childTypeMap = collectDetailChildTypeMap(fields, detailSheet.detailKey)
  const numericChildKeys = collectDetailNumericChildKeys(fields, detailSheet.detailKey)
  // 规则 2：明细清单列 ≥1；FIELD key ∈ 子字段 key 全集（全部子类型允许）；SYSTEM key 白名单含 status（与实例级对齐）
  const columns = detailSheet.columns || []
  if (columns.length === 0) {
    return `明细清单列至少配置 1 列`
  }
  for (const col of columns) {
    const type = String(col?.type ?? '').toUpperCase()
    if (type === 'SYSTEM') {
      if (!STATS_LIST_SYSTEM_COLUMN_KEYS.includes(col.key)) {
        return `明细清单列不支持的系统字段：${col.key}`
      }
    } else if (type === 'FIELD') {
      if (!childKeys.has(col.key)) {
        return `明细清单列引用了已删除的字段：${col.key}`
      }
    } else {
      return `明细清单列存在不支持的列类型：${col?.type}`
    }
  }
  // 规则 3：明细汇总维度 1-3 个；FIELD 限子字段 SELECT/RADIO/DATE/DATETIME；SYSTEM key 白名单含 month（与实例级对齐）
  const dimensions = detailSheet.dimensions || []
  if (dimensions.length < STATS_DIMENSION_MIN || dimensions.length > STATS_DIMENSION_MAX) {
    return `明细汇总维度需 ${STATS_DIMENSION_MIN}-${STATS_DIMENSION_MAX} 个`
  }
  for (const dim of dimensions) {
    const type = String(dim?.type ?? '').toUpperCase()
    if (type === 'SYSTEM') {
      if (!STATS_SUMMARY_SYSTEM_DIMENSION_KEYS.includes(dim.key)) {
        return `明细汇总维度不支持的系统字段：${dim.key}`
      }
    } else if (type === 'FIELD') {
      if (!childKeys.has(dim.key)) {
        return `明细汇总维度引用了已删除的字段：${dim.key}`
      }
      if (!STATS_DETAIL_DIMENSION_FIELD_TYPES.includes(childTypeMap[dim.key])) {
        return `明细汇总维度字段类型不支持（仅 SELECT/RADIO/DATE/DATETIME）：${dim.key}`
      }
    } else {
      return `明细汇总维度存在不支持的维度类型：${dim?.type}`
    }
  }
  // 规则 4：明细汇总指标 ≥1；COUNT 的 key 必须为 *（= 明细行数）；其余限子字段数值列（子类型 NUMBER/MONEY）
  const metrics = detailSheet.metrics || []
  if (metrics.length === 0) {
    return `明细汇总指标至少配置 1 个`
  }
  for (const metric of metrics) {
    const aggregate = String(metric?.aggregate ?? '').toUpperCase()
    if (!STATS_AGGREGATES.includes(aggregate)) {
      return `明细汇总指标不支持的聚合方式：${metric?.aggregate}`
    }
    if (aggregate === 'COUNT') {
      if (metric.key !== STATS_COUNT_KEY) {
        return `明细汇总 COUNT 指标的 key 必须为 *`
      }
      continue
    }
    if (!childKeys.has(metric.key)) {
      return `明细汇总指标引用了已删除的字段：${metric.key}`
    }
    if (!numericChildKeys.has(metric.key)) {
      return `明细汇总 ${aggregate} 指标仅支持数值字段：${metric.key}`
    }
  }
  return ''
}

/**
 * 校验统计导出时间区间（spec §5.3：起止必填、start ≤ end、跨度 ≤ 12 个月，前后端同规则）
 * 跨度口径：endDate ≤ startDate + 12 个自然月（同日界内）
 * @param {string} startDate 开始日期（yyyy-MM-dd）
 * @param {string} endDate 结束日期（yyyy-MM-dd）
 * @returns {string} 空串表示通过；否则为错误提示
 */
export function validateStatsExportRange(startDate, endDate) {
  if (!startDate || !endDate) return '请选择导出时间区间'
  const start = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '导出时间区间格式不正确'
  if (start.getTime() > end.getTime()) return '开始日期不能晚于结束日期'
  const limit = new Date(start.getTime())
  limit.setMonth(limit.getMonth() + STATS_EXPORT_RANGE_MAX_MONTHS)
  if (end.getTime() > limit.getTime()) return `导出时间跨度不能超过 ${STATS_EXPORT_RANGE_MAX_MONTHS} 个月`
  return ''
}
